import { useMemo, useCallback, useEffect } from "react";

import { Button } from "@kelp-dao/kernel-design-system";

import { IS_STAKING_PAUSED, toFixedWithoutRounding } from "@constants/common";
import { useHgEthDeposit } from "./Vault/HighGrowthVault/hgEthHooks";
import { Text } from "@chakra-ui/react";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";
import { useRestakeApy } from "@hooks/useRestakeApy";
import { trackEvent } from "@lib/utils";
import { STAKE_WITH_GAIN_CTA } from "@constants/analytics";

const MODAL_STRINGS = {
  TITLE: "Deposit with Gain",
  BUTTON_TEXT: "Deposit in Gain vault",
  CONFIRM_BUTTON_TEXT: "Deposit now",
  DESCRIPTION:
    "By depositing with Gain, your ETH will be automatically staked and start earning higher yields immediately.",
  DEPOSIT_LABEL: "Asset to deposit",
  RECEIVE_LABEL: "You will receive",
  APR_LABEL: "Expected APR",
  APR_FALLBACK: "-",
} as const;

// Types
interface StakeWithHgEthProps {
  amount: string;
  selectedToken: string;
  error: string;
  isConnected: boolean;
  isPoolSizeExceeded?: boolean;
  variant?: "primary" | "outline";
}

// Main component
const StakeWithHgEth = ({
  amount,
  selectedToken,
  error,
  isConnected,
  isPoolSizeExceeded = false,
  variant = "primary",
}: StakeWithHgEthProps) => {
  const { handleClick, handleChange, handleTokenSelect, checkInput } =
    useHgEthDeposit();

  useEffect(() => {
    if (amount) {
      handleChange(amount);
    }
  }, [amount, handleChange]);

  const { smartAPY: apy, isLoading: isVaultTvlApyLoading } =
    useVaultTvlApy("hgETH");
  const { calculatedTotalAPY } = useRestakeApy();

  const hgETHApy =
    apy && calculatedTotalAPY
      ? (
          +toFixedWithoutRounding(apy.toString(), 2) - +calculatedTotalAPY
        ).toFixed(1)
      : null;

  const vaultError = useMemo(() => {
    return checkInput(amount);
  }, [amount, checkInput]);

  const isDisabled = useMemo(() => {
    return (
      Boolean(error) ||
      Boolean(vaultError) ||
      (isConnected && IS_STAKING_PAUSED) ||
      (isPoolSizeExceeded && isConnected) ||
      (!amount && isConnected)
    );
  }, [error, vaultError, isConnected, isPoolSizeExceeded, amount]);

  const handleDepositConfirm = useCallback(() => {
    trackEvent(STAKE_WITH_GAIN_CTA, {
      vault_name: "high_growth",
      amount: amount,
    });
    handleClick(amount, true);
  }, [handleClick, amount]);

  // Sync selected token with hook when it changes
  useEffect(() => {
    if (selectedToken) {
      handleTokenSelect(selectedToken);
    }
  }, [selectedToken, handleTokenSelect]);

  return (
    <>
      {hgETHApy !== null && !isVaultTvlApyLoading ? (
        <div className="flex flex-col gap-2">
          <Button
            variant={variant}
            className={`w-full py-3 mt-2 text-[12px] md:text-[14px] 2xl:text-[18px] ${
              isDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleDepositConfirm}
            disabled={isDisabled}
          >
            <div className="flex items-center gap-1">
              {MODAL_STRINGS.BUTTON_TEXT}

              <div
                className={`flex items-center rounded-[8px] py-[6px] px-1 ${
                  isDisabled ? "bg-[#9CA3AF]" : "bg-[#387B7B]"
                }`}
              >
                <Text
                  fontSize="14px"
                  lineHeight="18px"
                  fontWeight={400}
                  color="#FFF"
                >
                  +{hgETHApy}% APR
                </Text>
              </div>
            </div>
          </Button>
          {amount && !error && vaultError ? (
            <Text
              fontSize="12px"
              lineHeight="16px"
              fontWeight={500}
              color="#E73F3F"
            >
              {vaultError}
            </Text>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default StakeWithHgEth;
